import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useEffect, useState } from 'react';
import {
  DEFAULT_PIPELINE_DEFINITION,
  getPhaseLabels,
  isValidPhase,
  PHASES_ORDER,
} from '@internal/spassu-code-contract';
import type {
  CockpitArtifactView,
  CockpitSectionViewState,
} from '../model/cockpitViewModel';
import {
  coverageLabel,
  limitationLabel,
  pipelinePhaseLabel,
  sectionStatusLabel,
} from '../model/semanticLabels';
import type { ArtifactContentAccess } from './ArtifactJourneyItem';
import { ArtifactJourneyItem } from './ArtifactJourneyItem';
import { useCockpitStyles } from './cockpitStyles';

export type ArtifactsSectionProps = ArtifactContentAccess & {
  artifacts: CockpitArtifactView[];
  sectionState: CockpitSectionViewState;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Label of the phase the list is filtered by (RN-COCK-09); `null` lists every phase. */
  filterLabel?: string | null;
  onClearFilter?: () => void;
};

interface ArtifactGroup {
  key: string;
  label: string;
  /** Each entry keeps its position in the overall observed order. */
  items: Array<{ artifact: CockpitArtifactView; index: number }>;
}

const ArtifactGroupList = ({
  group,
  total,
  selectedId,
  onSelect,
  ...content
}: Omit<ArtifactsSectionProps, 'artifacts' | 'sectionState'> & {
  group: ArtifactGroup;
  total: number;
}) => {
  const classes = useCockpitStyles();
  return (
    <div className={classes.artifactGroup}>
      <Typography
        component="h3"
        id={`artifact-group-${group.key}`}
        className={classes.artifactGroupTitle}
      >
        {group.label}
      </Typography>
      <ol
        className={classes.artifactSequence}
        aria-labelledby={`artifact-group-${group.key}`}
      >
        {group.items.map(({ artifact, index }) => (
          <ArtifactJourneyItem
            key={artifact.id}
            artifact={artifact}
            index={index}
            total={total}
            open={selectedId === artifact.id}
            onSelect={onSelect}
            {...content}
          />
        ))}
      </ol>
    </div>
  );
};

/**
 * The documents the activity produced, grouped by pipeline phase in the
 * contract's order and, inside a phase, in the observed order of generation.
 * Closed by default like the timeline (RN-COCK-09): it opens on its own as
 * soon as an artifact is selected, from the stepper, the rail or a link, so a
 * selection is never hidden. Opening an item reveals the authorized Markdown
 * in place.
 */
export const ArtifactsSection = ({
  artifacts,
  sectionState,
  filterLabel = null,
  onClearFilter,
  ...rest
}: ArtifactsSectionProps) => {
  const classes = useCockpitStyles();
  const [expanded, setExpanded] = useState(
    () => rest.selectedId !== null || filterLabel !== null,
  );
  // a selection or a phase filter always reveals the list it points at
  useEffect(() => {
    if (rest.selectedId !== null || filterLabel !== null) setExpanded(true);
  }, [rest.selectedId, filterLabel]);
  const visibleArtifacts = uniqueArtifacts(artifacts);
  const groups = groupByPhase(visibleArtifacts);
  return (
    <Accordion
      component="section"
      className={classes.panel}
      aria-labelledby="artifact-sequence-title"
      expanded={expanded}
      onChange={(_, next) => setExpanded(next)}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        id="artifacts-header"
        aria-controls="artifacts-content"
        className={classes.collapsibleSummary}
        classes={{
          content: classes.collapsibleSummaryContent,
          expanded: classes.collapsibleExpanded,
        }}
        expandIcon={<ExpandMoreIcon />}
      >
        <div>
          <Typography
            id="artifact-sequence-title"
            component="h2"
            className={classes.sectionTitle}
          >
            Artefatos
          </Typography>
          <Typography variant="body2" className={classes.sectionLede}>
            {filterLabel ? `Etapa ${filterLabel} · ` : ''}
            {sectionStatusLabel(sectionState.status)} ·{' '}
            {coverageLabel(sectionState.coverage)} · {visibleArtifacts.length}{' '}
            {visibleArtifacts.length === 1 ? 'artefato' : 'artefatos'}.
            {filterLabel
              ? ' Em ordem de geração; abra um artefato para ler o conteúdo autorizado.'
              : ' Agrupados por fase, em ordem de geração; abra um artefato para ler o conteúdo autorizado.'}
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails
        id="artifacts-content"
        className={classes.collapsibleDetails}
      >
        <div>
          {filterLabel && onClearFilter && (
            <button
              type="button"
              className={classes.metricDetailsToggle}
              onClick={onClearFilter}
            >
              Ver todos os artefatos
            </button>
          )}
          {visibleArtifacts.length === 0 ? (
            <Typography>
              Nenhum artefato foi observado nesta revisão.
            </Typography>
          ) : (
            <div
              className={classes.artifactGroups}
              role="group"
              aria-label="Ordem observada de geração dos artefatos"
            >
              {groups.map(group => (
                <ArtifactGroupList
                  key={group.key}
                  group={group}
                  total={visibleArtifacts.length}
                  {...rest}
                />
              ))}
            </div>
          )}
          {sectionState.limitations.length > 0 && (
            <Typography variant="caption">
              Limitações:{' '}
              {sectionState.limitations.map(limitationLabel).join('; ')}
            </Typography>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

function uniqueArtifacts(
  artifacts: CockpitArtifactView[],
): CockpitArtifactView[] {
  const identities = new Set<string>();
  return artifacts.filter(artifact => {
    if (identities.has(artifact.id)) return false;
    identities.add(artifact.id);
    return true;
  });
}

const UNPHASED_KEY = 'unphased';

// Phase groups follow the contract order; artifacts whose phase is absent or
// unknown gather at the end, after every canonical phase, never interleaved.
function groupByPhase(artifacts: CockpitArtifactView[]): ArtifactGroup[] {
  const labels = getPhaseLabels(DEFAULT_PIPELINE_DEFINITION);
  const byKey = new Map<string, ArtifactGroup>();
  artifacts.forEach((artifact, index) => {
    const phase = artifact.phase;
    const key = phase && isValidPhase(phase) ? phase : UNPHASED_KEY;
    const group = byKey.get(key) ?? {
      key: key === UNPHASED_KEY ? key : `phase-${PHASES_ORDER.indexOf(key)}`,
      label:
        key === UNPHASED_KEY ? pipelinePhaseLabel(null) : labels[key as never],
      items: [],
    };
    group.items.push({ artifact, index });
    byKey.set(key, group);
  });
  const ordered = PHASES_ORDER.map(phase => byKey.get(phase)).filter(
    (group): group is ArtifactGroup => group !== undefined,
  );
  const unphased = byKey.get(UNPHASED_KEY);
  return unphased ? [...ordered, unphased] : ordered;
}
